/**
 * Naturality Squares — pure game engine (no React, no DOM, no unseeded
 * randomness).
 *
 * A second game for this app (distinct from Square Stitcher's rotate-to-match
 * mechanic): it dramatizes naturality directly. A 2-cell rewrite preserves the
 * safety boundary exactly when it is natural — when the naturality square
 * commutes. The player is handed a square with three sides fixed (a top
 * morphism and two vertical legs) and must choose the bottom rewrite that makes
 * both composite paths agree: down-then-across must equal across-then-down. Only
 * the natural rewrite makes the square commute; a non-natural rewrite would
 * carry the safety boundary off its target, and there is no commuting slot for
 * it. The boundary-crossing 2-cell is unconstructable, not flagged after the
 * fact.
 *
 * Composition is modeled as composition of permutations of a small boundary, so
 * commuting is exact and decidable. The arcade layer lives in the panel.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

/**
 * A morphism on the boundary, a permutation of [0..n). Composing morphisms is
 * composing permutations, so two paths commute iff their composite permutations
 * are equal — an exact, decidable naturality check.
 */
export interface BoundaryMorphism {
  readonly id: string;
  readonly label: string;
  /** perm[i] = image of boundary slot i. Length defines the boundary size. */
  readonly perm: readonly number[];
}

/** A square to complete: top + two legs fixed; choose the bottom rewrite. */
export interface NaturalitySquare {
  readonly id: string;
  /** The fixed top morphism (A → B). */
  readonly top: BoundaryMorphism;
  /** The left leg (A → A'). */
  readonly left: BoundaryMorphism;
  /** The right leg (B → B'). */
  readonly right: BoundaryMorphism;
  /** Candidate bottom rewrites (A' → B'); exactly one makes the square commute. */
  readonly candidates: readonly [BoundaryMorphism, ...BoundaryMorphism[]];
}

/** A round: a sequence of squares to complete. */
export interface NaturalityLevel {
  readonly id: string;
  readonly name: string;
  readonly brief: string;
  readonly squares: readonly [NaturalitySquare, ...NaturalitySquare[]];
}

export type SquaresStatus = "playing" | "cleared";

export type SquaresEvent = "none" | "commuted" | "no-slot";

export interface SquaresState {
  readonly levelId: string;
  readonly squareIndex: number;
  readonly solved: number;
  readonly status: SquaresStatus;
  readonly lastEvent: SquaresEvent;
}

export type SquaresIntent =
  | { readonly kind: "place"; readonly candidateId: string }
  | { readonly kind: "advance" };

/** Compose two permutations: (g ∘ f)(i) = g(f(i)). Both must be same length. */
export function composePerm(
  f: readonly number[],
  g: readonly number[],
): readonly number[] {
  return f.map((fi) => g[fi]);
}

export function permEqual(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

export function currentSquare(
  level: NaturalityLevel,
  state: SquaresState,
): NaturalitySquare | null {
  return level.squares[state.squareIndex] ?? null;
}

export function candidateById(
  square: NaturalitySquare,
  id: string,
): BoundaryMorphism | null {
  return square.candidates.find((c) => c.id === id) ?? null;
}

/**
 * The naturality check: down-then-across (left then bottom) must equal
 * across-then-down (top then right). The square commutes iff
 *   bottom ∘ left = right ∘ top.
 */
export function squareCommutes(
  square: NaturalitySquare,
  bottom: BoundaryMorphism,
): boolean {
  const downAcross = composePerm(square.left.perm, bottom.perm);
  const acrossDown = composePerm(square.top.perm, square.right.perm);
  return permEqual(downAcross, acrossDown);
}

/**
 * The legal-move generator: a candidate may be placed only if it makes the
 * square commute. A non-natural rewrite has no commuting slot — placing it is a
 * non-structural miss that does not advance.
 */
export function placementIsNatural(
  square: NaturalitySquare,
  candidateId: string,
): boolean {
  const bottom = candidateById(square, candidateId);
  return bottom !== null && squareCommutes(square, bottom);
}

export function createSquaresState(level: NaturalityLevel): SquaresState {
  return {
    levelId: level.id,
    squareIndex: 0,
    solved: 0,
    status: "playing",
    lastEvent: "none",
  };
}

export function squaresProgress(
  level: NaturalityLevel,
  state: SquaresState,
): number {
  if (level.squares.length === 0) return 1;
  return state.solved / level.squares.length;
}

/** The pure reducer: the single source of truth for naturality. */
export function squaresReducer(
  level: NaturalityLevel,
  state: SquaresState,
  intent: SquaresIntent,
): SquaresState {
  if (state.status !== "playing") return state;
  const square = currentSquare(level, state);
  if (square === null) return state;

  if (intent.kind === "advance") return state; // advancing is automatic on solve

  // intent.kind === "place"
  if (!placementIsNatural(square, intent.candidateId)) {
    // A non-natural rewrite has no commuting slot: it does not place.
    return { ...state, lastEvent: "no-slot" };
  }
  const solved = state.solved + 1;
  const cleared = solved >= level.squares.length;
  // Advance past the solved square unconditionally — including off the end on
  // clear — so currentSquare() returns null once the round is cleared and the
  // engine/panel contract stays consistent (no current square after clearing).
  return {
    ...state,
    squareIndex: state.squareIndex + 1,
    solved,
    status: cleared ? "cleared" : "playing",
    lastEvent: "commuted",
  };
}

/**
 * The forbidden generator made explicit for tests and docs: there is no
 * commuting slot for a non-natural rewrite. Always reports false — a
 * boundary-crossing 2-cell is unconstructable.
 */
export function nonNaturalRewriteGenerated(): false {
  return false;
}
